import { useCallback, useState } from 'react';

import { useModifyAccount, useUserSettings } from '@/hooks/useAccounts';
import { UseMyOptions } from '@/hooks/useMy';
import useValidateIntegration, {
  ValidateIntegrationErrorResponse,
  ValidateIntegrationOptions,
} from '@/hooks/useValidateIntegration';
import { Account, JiraProject, JiraTemplate } from '@/types';

export const useIntegrationSettings = (
  provider = 'jira',
  value: string = '',
  options?: UseMyOptions<'account'>
) => {
  const { validateIntegration, isValidating } = useValidateIntegration();
  const {
    data: integrationAccounts,
    status,
    error,
    invalidate,
  } = useUserSettings(options, `${provider}#${value}`);
  const { mutateAsync: updateAccount, status: updateAccountStatus } =
    useModifyAccount('update');

  const data: Account = (integrationAccounts || [])?.find(
    account => account.value === value
  ) as Account;

  const [projects, setProjects] = useState<JiraProject[]>([]);
  const [templatesError, setTemplatesError] = useState<
    ValidateIntegrationErrorResponse['templates']
  >({});
  const [isValid, setIsValid] = useState(false);

  const getIntegrationProjects = useCallback(
    async (searchTerm?: string) => {
      if (!data?.key) return { projects: [] };
      const { projects } = await validateIntegration({
        provider,
        account: data.key,
        settings: searchTerm !== undefined ? { searchTerm } : undefined,
      });
      setProjects(projects);
      return { projects };
    },
    [data?.key, provider, validateIntegration]
  );

  const searchProjects = useCallback(
    async (searchTerm: string, config?: Account['config']) => {
      // For existing integrations, use the account key
      // For new integrations, use the provided config (credentials)
      if (!data?.key && !config) return [];

      const { projects } = await validateIntegration({
        provider,
        account: data?.key,
        config: data?.key ? undefined : config,
        settings: { searchTerm },
      });
      return projects;
    },
    [data?.key, provider, validateIntegration]
  );

  async function onValidateIntegration(
    {
      account,
      config,
      settings,
      templateId,
    }: {
      account: string;
      config: Account['config'];
      settings: Account['settings'];
      templateId?: string;
    },
    options?: ValidateIntegrationOptions
  ) {
    const { projects, isValid, templatesError } = await validateIntegration({
      provider,
      account,
      config,
      settings,
      templateId,
      suppressErrorToast: options?.suppressErrorToast,
    });

    isValid && config && projects && setProjects(projects);
    templatesError &&
      Object.keys(templatesError).length &&
      setTemplatesError(templatesError);
    setIsValid(isValid);

    return { projects, isValid, templatesError };
  }

  async function onDisconnectTemplate(template: JiraTemplate) {
    const templates = data.settings?.templates ?? [];
    const newTemplates = templates.filter(
      current => current.id !== template.id
    );
    await updateAccount({
      ...data,
      username: provider,
      settings: {
        ...data.settings,
        templates: newTemplates,
      },
    });
    invalidate();
  }

  return {
    data,
    error,
    getIntegrationProjects,
    isValid,
    onDisconnectTemplate,
    onValidateIntegration,
    projects: projects ?? [],
    searchProjects,
    setProjects,
    setTemplatesError,
    templatesError,
    status: {
      isValidating,
      accountStatus: status,
      disconnectProjectStatus: updateAccountStatus,
    },
  };
};
